import React, {useState, useEffect} from 'react';
import {Formik, Form, useFormikContext} from 'formik';
import {OverlayTrigger, Tooltip} from 'react-bootstrap';
import * as yup from 'yup';
import 'yup-phone';
import _ from 'lodash';
import moment from 'moment';

import {
  TextInput,
  FileInput,
  InputWithSuggestions,
  Checkbox,
} from '../../components/Forms';
import Button, {ButtonTypes, ButtonSizes} from '../../components/ButtonNew';
import Card from '../../components/Card';
import CollapserThing from '../../components/CollapserThing';
import InlineNotification from '../../components/InlineNotification';
import Link from '../../components/Link';
import {InformationIcon} from '../../components/svg';

import {useAppContext, useAnalytics} from '../../hooks';
import {getTags, getMyJobReferralRequests, createJobReferralRequest} from '../../api/api';
import {encodeFileAsync} from '../../util';
import {
  REFERRAL_REQUEST_TTL,
  MAX_REFERRAL_REQUESTS_PER_PERIOD,
  MEMBER_MAX_REFERRAL_REQUESTS_PER_PERIOD,
} from '../../../../lib/constants/job_referrals';
import {Feature} from '../../../../lib/constants/tags';
import {Searcher} from '../../../../lib/fuzzySearch';

const Resume = () => {
  const {setFieldValue} = useFormikContext();
  return (
    <FileInput
      label="Your resume (PDF only, max 7MB)"
      id="resume"
      name="resume"
      accept="application/pdf"
      onChange={(event) => setFieldValue('resume', event.currentTarget.files[0])}
      required
    />
  );
};

const initialValues = {
  company: '',
  role: '',
  jobURL: '',
  phone: '',
  linkedin: '',
  resume: null,
};

const SimpleFormSchema = yup.object({
  role: yup.string().required('Required'),
  linkedin: yup.string().matches(/linkedin\.com\//, 'Must be valid LinkedIn URL').required('Required'),
  resume: yup.mixed().required('Required'),
  urmStatus: yup.bool(),
});

export default function ReferralRequestForm({company: initialCompany, isLayoff}) {
  const {hasSubscription} = useAppContext();
  const {tracker} = useAnalytics();
  const [tags, setTags] = useState(null);
  const [error, setError] = useState(null);
  const [requestedCompanySearcher, setRequestedCompanySearcher] = useState(null);
  const [companySearcher, setCompanySearcher] = useState(null);
  const [requestedCompanyResults, setRequestedCompanyResults] = useState([]);
  const [currentCompanyResults, setCurrentCompanyResults] = useState([]);
  const [referrerCompanies, setReferrerCompanies] = useState([]);

  const [submittedRequest, setSubmittedRequest] = useState(null);
  const [canRequest, setCanRequest] = useState(true);

  useEffect(() => {
    const setUpSearchers = async () => {
      const tagResults = await getTags({feature: Feature.Referrals});

      // Companies are listed by active referrer count from the job_referrer table
      const companiesWithReferrers = tagResults.company.filter((company) => company.count > 0);
      const requestedSearcher = new Searcher(companiesWithReferrers, ['name']);
      const searcher = new Searcher(tagResults.company, ['name']);

      setReferrerCompanies(companiesWithReferrers);
      setTags(tagResults);
      setRequestedCompanySearcher(requestedSearcher);
      setCompanySearcher(searcher);

      setRequestedCompanyResults(requestedSearcher.search());
      setCurrentCompanyResults(searcher.search());
    };

    const checkIfCanRequest = async () => {
      const createdAfter = moment().subtract(REFERRAL_REQUEST_TTL, 'days').toISOString();
      const recentRequests = await getMyJobReferralRequests({createdAfter});
      if (hasSubscription && recentRequests.length >= MEMBER_MAX_REFERRAL_REQUESTS_PER_PERIOD) {
        setCanRequest(false);
      } else if (!hasSubscription && recentRequests.length >= MAX_REFERRAL_REQUESTS_PER_PERIOD) {
        setCanRequest(false);
      }
    };

    setUpSearchers();
    checkIfCanRequest();
    tracker.jobReferralRequestView();
  }, []);

  const FormSchema = yup.object({
    company: yup.string().oneOf(_.map(referrerCompanies, 'name'), "Sorry, we don't have any referrers for this company. Please choose from the available ones.").required('Required'),
    openToAllCompanies: yup.bool(),
    currentCompany: yup.string(),
    role: yup.string().required('Required'),
    jobURL: yup.string().url('Must be a valid URL').required('Required'),
    phone: yup.string().phone('US', false, 'Must be a valid phone number').required('Required'),
    linkedin: yup.string().matches(/linkedin\.com\//, 'Must be valid LinkedIn URL').required('Required'),
    resume: yup.mixed().required('Required'),
    urmStatus: yup.bool(),
  });

  const displayNameToSlug = (companyName) => {
    const company = _.find(tags?.company ?? [], ['name', companyName]);
    return company ? company.id : companyName;
  };

  const toAPIFormat = async ({
    company,
    openToAllCompanies,
    currentCompany,
    role,
    jobURL,
    phone,
    linkedin,
    resume,
    urmStatus,
    layoffStatus,
  }) => ({
    company: displayNameToSlug(company),
    openToAllCompanies,
    currentCompany: displayNameToSlug(currentCompany),
    role,
    jobURL,
    phone,
    linkedin,
    resume: await encodeFileAsync(resume),
    urmStatus,
    layoffStatus,
  });

  const updateRequestedCompanySuggestions = (query) => {
    setRequestedCompanyResults(requestedCompanySearcher.search(query));
  };

  const updateCurrentCompanySuggestions = (query) => {
    setCurrentCompanyResults(companySearcher.search(query));
  };

  const handleSubmit = async (values, {resetForm, setSubmitting}) => {
    setError(null);
    setSubmitting(true);
    tracker.jobReferralRequestSubmit();
    try {
      const data = await toAPIFormat({...values, layoffStatus: isLayoff});
      const request = await createJobReferralRequest(data);
      setSubmittedRequest(request);
      resetForm(); // TODO: not working
      tracker.jobReferralRequestSuccessfulSubmit();
      setSubmitting(false);
    } catch (err) {
      setError(err);
      setSubmitting(false);
    }
  };

  return (
    <>
      <h1 className="w-full block my-2 md:my-4 text-xl md:text-4xl text-center font-bold text-gray-900">
        {isLayoff ? `Exponent Layoff Referrals (${new Date().getFullYear()})` : 'Submit a referral request'}
      </h1>

      <span className="block w-full md:w-4/5 max-w-xl mx-auto mb-1 text-sm md:text-base text-center font-medium leading-tight text-gray-600">
        {isLayoff ? (
          <span>
            {`Use this form to submit a referral request if you were recently
            laid off from your job in ${new Date().getFullYear()}. Otherwise, please use the `}
            <Link className="text-indigo-600" href="/jobs/referrals/request">Referral Request form</Link>
            .
          </span>
        ) : 'Request to be referred by other Exponent members for your desired company and job. Referrers will reach out if there\'s a match.'}

      </span>

      <Formik
        initialValues={{...initialValues, company: initialCompany?.name}}
        validationSchema={isLayoff ? SimpleFormSchema : FormSchema}
        onSubmit={handleSubmit}
      >
        {({isSubmitting}) => (
          <Form className="w-full flex flex-col items-center mt-10">
            <div className="w-full lg:w-3/5">
              <Card className="mb-5">
                {submittedRequest ? (
                  <span className="block w-full mx-auto mb-1 text-sm md:text-base text-center font-medium leading-tight text-gray-600">
                    Sweet! Your request is live and being shared with
                    Exponent members who can refer you.
                    {!hasSubscription && (
                      <>
                        {' '}
                        Want more visibility? Show up at the the top of the candidate list by
                        {' '}
                        <Link href="/upgrade?src=submitted_job_referral_request" className="text-indigo font-semibold">becoming a member</Link>
                        .
                      </>
                    )}
                  </span>
                ) : (
                  <fieldset disabled={!canRequest || isSubmitting}>
                    <CollapserThing visible={!canRequest && !isLayoff}>
                      <InlineNotification
                        className="mb-5"
                        ctaText={hasSubscription ? null : 'Sign up'}
                        href={`${hasSubscription ? '/jobs/referrals' : '/upgrade'}?src=max_job_referral_requests`}
                      >
                        {hasSubscription
                          ? `Oops! You've reached your limit of ${MEMBER_MAX_REFERRAL_REQUESTS_PER_PERIOD} referral requests for the month. Come back next month!`
                          : `Oops! You've reached your limit of ${MAX_REFERRAL_REQUESTS_PER_PERIOD} referral requests for the month. Need a referral sooner? Exponent members can submit up to ${MEMBER_MAX_REFERRAL_REQUESTS_PER_PERIOD} referral requests every month.`}
                      </InlineNotification>
                    </CollapserThing>


                    {!isLayoff && (
                      <InputWithSuggestions
                        label="Requested company"
                        name="company"
                        type="text"
                        placeholder="Search companies"
                        updateSuggestions={updateRequestedCompanySuggestions}
                        suggestions={requestedCompanyResults}
                        open={!initialCompany}
                        required
                      />
                    )}
                    <OverlayTrigger
                      placement="left"
                      overlay={hasSubscription ? (
                        <Tooltip>
                          Selecting this option makes your referral request
                          available to the broader referrer community
                        </Tooltip>
                      ) : (
                        <Tooltip>
                          Upgrade your account to unlock this feature
                        </Tooltip>
                      )}
                    >
                      <Checkbox
                        label="I'd like to be considered for referrals at other companies as well"
                        disabled={!hasSubscription}
                        name="openToAllCompanies"
                      />
                    </OverlayTrigger>

                    {/* Could be an autocomplete with custom option */}
                    <TextInput
                      label="Role title"
                      name="role"
                      type="text"
                      placeholder="Product Designer, Software Engineer, etc."
                      required
                    />

                    {!isLayoff && (
                      <>
                        <TextInput
                          label="Job listing URL"
                          name="jobURL"
                          type="text"
                          required
                        />
                        <TextInput
                          label="Your phone number"
                          name="phone"
                          type="text"
                          placeholder="(123) 456-7890"
                          required
                        />
                      </>
                    )}

                    <TextInput
                      label="Your LinkedIn profile"
                      name="linkedin"
                      type="text"
                      placeholder="https://linkedin.com/..."
                      required
                    />

                    <Resume />

                    <InputWithSuggestions
                      label="Your current company"
                      name="currentCompany"
                      type="text"
                      placeholder="Search companies"
                      updateSuggestions={updateCurrentCompanySuggestions}
                      suggestions={currentCompanyResults}
                    />

                    <Checkbox
                      label="(Optional) I self-identify as a member of an under-represented group."
                      name="urmStatus"
                    />

                    {(!hasSubscription && !isLayoff) && (
                      <span className="block w-full md:w-4/5 mx-auto mb-1 text-sm md:text-base text-center font-medium leading-tight text-gray-600">
                        Exponent members get priority-access to our referral network.
                        Boost your referral request by
                        {' '}
                        <Link href="/upgrade?src=job_referral_request_bottom" className="text-indigo font-semibold" target="_blank">
                          becoming a member.
                        </Link>
                      </span>
                    )}
                  </fieldset>
                )}
                <div className="flex mt-4 w-full">
                  {submittedRequest ? (
                    <div className="w-full">
                      <Button
                        className="w-full"
                        size={ButtonSizes.LARGE}
                        onClick={() => setSubmittedRequest(null)}
                      >
                        Submit another
                      </Button>
                      <Button
                        href="/jobs/referrals/requests"
                        className="w-full mt-2"
                        size={ButtonSizes.LARGE}
                        buttonType={ButtonTypes.TERTIARY}
                      >
                        View requests
                      </Button>
                    </div>
                  ) : (
                    <Button
                      type="submit"
                      className="w-full"
                      size={ButtonSizes.LARGE}
                      loading={isSubmitting}
                      disabled={!canRequest}
                      onClick={() => {}}
                    >
                      Submit
                    </Button>
                  )}
                </div>
                {error && (
                  <div className="mt-2 text-base font-medium leading-tight text-red-600">
                    {error.message}
                  </div>
                )}
              </Card>
            </div>
          </Form>
        )}
      </Formik>
    </>
  );
}
